import { useState, useCallback } from 'react';

/**
 * @returns Object with form validation utilities
 *
 * @example
 * ```tsx
 * const {
 *   markFieldAsTouched,
 *   isFieldTouched,
 *   clearTouchedFields,
 * } = useFormValidation();
 *
 * // Mark a field as touched when user interacts
 * <Field onChange={() => markFieldAsTouched('email')} />
 *
 * // Show validation only if field was touched
 * {isFieldTouched('email') && <ValidationError />}
 * ```
 */
export const useFormValidation = () => {
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Mark a field as touched
  const markFieldAsTouched = useCallback((fieldName: string) => {
    setTouchedFields((prev) => new Set(Array.from(prev).concat(fieldName)));
  }, []);

  // Check if a field is touched
  const isFieldTouched = useCallback(
    (fieldName: string) => {
      return touchedFields.has(fieldName);
    },
    [touchedFields],
  );

  // Clear all touched fields
  const clearTouchedFields = useCallback(() => {
    setTouchedFields(new Set());
  }, []);

  // Mark multiple fields as touched
  const markMultipleFieldsAsTouched = useCallback((fieldNames: string[]) => {
    setTouchedFields((prev) => new Set([...Array.from(prev), ...fieldNames]));
  }, []);

  // Get all touched fields
  const getTouchedFields = useCallback(() => {
    return Array.from(touchedFields);
  }, [touchedFields]);

  return {
    // Business logic
    markFieldAsTouched,
    isFieldTouched,
    clearTouchedFields,
    markMultipleFieldsAsTouched,
    getTouchedFields,

    // State
    touchedFields,
  };
};
